import { memo } from "react";

import useImageUtils from "~/hooks/useImageUtils";
import type { Image as ImageType } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import { generateImageParams } from "~/utils/salesforce-image-transform";

import Image from "../ui/image";

type ProductImageType = "plp" | "pdp" | "cart" | "orderCard";

/** Per-type responsive width/quality config. */
const SIZE_CONFIG: Record<
  ProductImageType,
  {
    desktop: number;
    tablet: number;
    mobile: number;
    qDesktop: number;
    qTablet: number;
    qMobile: number;
    square: boolean;
    sizes: string;
  }
> = {
  plp: {
    desktop: 306,
    tablet: 320,
    mobile: 280,
    qDesktop: 85,
    qTablet: 85,
    qMobile: 80,
    square: true,
    sizes: "(min-width: 1024px) 306px, (min-width: 768px) 320px, 280px",
  },
  // PDP main image renders in a ~575px box. desktop 700 → 2x = 1400 stays crisp
  // there (2x needs ~1150) without the prior 1200→2400 retina overfetch.
  pdp: {
    desktop: 700,
    tablet: 700,
    mobile: 500,
    qDesktop: 90,
    qTablet: 85,
    qMobile: 80,
    square: false,
    sizes: "(min-width: 1024px) 700px, (min-width: 768px) 700px, 500px",
  },
  // Small fixed thumbnail (cart/mini-cart line items, favorites, order
  // confirmation). Box maxes out at 160px (h-40), so a single 160→320 (2x)
  // srcSet covers every viewport — no per-breakpoint variance needed.
  cart: {
    desktop: 160,
    tablet: 160,
    mobile: 160,
    qDesktop: 80,
    qTablet: 80,
    qMobile: 80,
    square: false,
    sizes: "160px",
  },
  // Order-history row card. Its own entry rather than reusing `cart`: that box
  // is 160px and this one is 194px on desktop, so `cart`'s `sizes: "160px"`
  // would have the browser pick the 160px source and upscale it 21% on any 1x
  // display. Mobile keeps the 70px thumbnail, but the source stays 194 — one
  // srcSet for both, and downscaling costs nothing visible.
  orderCard: {
    desktop: 194,
    tablet: 194,
    mobile: 194,
    qDesktop: 80,
    qTablet: 80,
    qMobile: 80,
    square: false,
    sizes: "(min-width: 768px) 194px, 70px",
  },
};

const ProductImageComponent = ({
  image,
  lazy,
  small,
  className,
  type = "plp",
  priority = false,
  ref,
  ...props
}: React.ComponentProps<"picture"> & {
  image?: ImageType;
  lazy?: boolean;
  small?: boolean;
  type?: ProductImageType;
  /**
   * COM-158: above-the-fold LCP candidates (first row of PLP tiles) render
   * eager + fetchPriority="high" so the browser fetches them immediately.
   */
  priority?: boolean;
}) => {
  const { getImageMediaLink } = useImageUtils();

  if (!image) return null;

  const cfg = SIZE_CONFIG[type];

  // Build one URL for a given width/quality/format (+ optional DPR), and a
  // `1x, 2x` srcSet pair, from the per-type config above.
  const link = (
    width: number,
    quality: number,
    format: "webp" | "jpg" | "auto",
    dpr?: number,
  ) =>
    getImageMediaLink(image, {
      params: generateImageParams({
        width,
        height: cfg.square ? width : undefined,
        scaleMode: "fit",
        quality,
        format,
        strip: true,
        ...(dpr ? { dpr } : {}),
      }),
    });
  const srcSet = (
    width: number,
    quality: number,
    format: "webp" | "jpg" | "auto",
  ) =>
    `${link(width, quality, format)} 1x, ${link(width, quality, format, 2)} 2x`;

  return (
    <picture className={cn("h-full w-full", className)} ref={ref} {...props}>
      {/* WebP sources for modern browsers */}
      {/* Desktop */}
      <source
        srcSet={srcSet(cfg.desktop, cfg.qDesktop, "webp")}
        media="(min-width:1024px)"
        type="image/webp"
      />

      {/* Tablet */}
      <source
        srcSet={srcSet(cfg.tablet, cfg.qTablet, "webp")}
        media="(min-width:768px)"
        type="image/webp"
      />

      {/* Mobile */}
      <source
        srcSet={srcSet(cfg.mobile, cfg.qMobile, "webp")}
        type="image/webp"
      />

      {/* Fallback JPEG sources */}
      {/* Desktop */}
      <source
        srcSet={srcSet(cfg.desktop, cfg.qDesktop, "jpg")}
        media="(min-width:1024px)"
      />

      {/* Default img tag for fallback */}
      <Image
        src={link(cfg.tablet, cfg.qTablet, "auto")}
        height={cfg.square ? cfg.desktop : cfg.tablet}
        width={cfg.square ? cfg.desktop : cfg.tablet}
        alt={image.alt || "image"}
        className={cn(
          "block h-full w-full object-cover object-center",
          className,
        )}
        loading={priority || lazy === false ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        sizes={cfg.sizes}
      />
    </picture>
  );
};
ProductImageComponent.displayName = "ProductImageComponent";

export const ProductImage = memo(
  ProductImageComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.image?.link === nextProps.image?.link &&
      prevProps.lazy === nextProps.lazy &&
      prevProps.small === nextProps.small &&
      prevProps.className === nextProps.className &&
      prevProps.type === nextProps.type &&
      prevProps.priority === nextProps.priority
    );
  },
);
ProductImage.displayName = "ProductImage";
